import {
  isSequenceApiError,
  isSequenceApiErrorResponse,
  type SequenceError
} from '@sequencehq/api'
import upperFirst from 'lodash/upperFirst'
import { match } from 'ts-pattern'

/**
 * The code NetSuite sync failures carry when the record was rejected, either
 * by NetSuite itself or by one of our own checks before the push.
 */
const NETSUITE_VALIDATION_REJECTED = 'VALIDATION_REJECTED'

/**
 * Our own pre-push checks end their reason with debug ids, like
 * "customer is not synced to NetSuite. customerId=abc". Everything from the
 * first `key=` on is for logs, not the user.
 */
const DEBUG_ID_SUFFIX = /\s\w+=.*$/s
const ERROR_CODE_SUFFIX = /\s+errorCode=.*$/s

/**
 * Explains why a push or sync to an integration failed, for the description
 * line of the error toast. Returns `undefined` when the backend gave no reason,
 * so the toast keeps just its title.
 *
 * A rejection's message is only a generic "NetSuite rejected the invoice
 * details.", so we add the actual reasons from its `detail`.
 */
export const getSyncErrorDescription = (error: unknown): string | undefined => {
  if (
    !isSequenceApiError(error) ||
    !isSequenceApiErrorResponse(error.response)
  ) {
    return undefined
  }

  // Typed as always present, but the backend can omit it.
  const errors: SequenceError[] = Array.isArray(error.response.error.errors)
    ? error.response.error.errors
    : []

  const sentences = errors.flatMap(item => [
    item.message.replace(ERROR_CODE_SUFFIX, '').trim(),
    ...match(item.code)
      .with(NETSUITE_VALIDATION_REJECTED, () =>
        getNetsuiteRejectionReasons(item.detail)
      )
      .otherwise(() => [])
  ])

  const description = sentences.filter(Boolean).join(' ')

  return description || undefined
}

/**
 * A rejection's `detail` is one of:
 *
 *   NetSuite's own body   {"o:errorDetails":[{"detail":"Invalid subsidiary."}]}
 *   our pre-push check    customer is not synced to NetSuite. customerId=abc
 *   a gateway error page  <html>Bad Request</html>
 *
 * The first two give reasons. Markup and JSON of any other shape give none,
 * rather than showing the user a raw response.
 */
const getNetsuiteRejectionReasons = (
  detail: SequenceError['detail']
): string[] => {
  if (typeof detail !== 'string' || !detail) {
    return []
  }

  const body = parseJson(detail)

  if (body === undefined) {
    return isMarkup(detail) ? [] : [formatPlainTextReason(detail)]
  }

  const hasErrorDetails =
    typeof body === 'object' && body !== null && 'o:errorDetails' in body

  if (!hasErrorDetails || !Array.isArray(body['o:errorDetails'])) {
    return []
  }

  return body['o:errorDetails'].flatMap(errorDetail =>
    typeof errorDetail?.detail === 'string' ? [errorDetail.detail] : []
  )
}

// JSON has no `undefined`, so it is a safe "did not parse" result.
const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

const isMarkup = (value: string): boolean => value.trimStart().startsWith('<')

const formatPlainTextReason = (detail: string): string =>
  upperFirst(detail.replace(DEBUG_ID_SUFFIX, '').trim())
